import type { SummaryListItemType } from '@/types'
import type { Part } from '@/schemas/general/types'
import { getPartTitle } from '@/helpers/shared'
import {
  type PdfNode,
  getCurrentLocale,
  getPdfMake,
  setupPdfMakeFonts,
  preventTitleWidow,
  createListSection,
  trimTrailingSectionSpacing,
  buildFooter,
  getSharedStyles,
  getPdfAuthor,
} from './pdf-shared'

interface PdfProject {
  [key: string]: Part | null
}

interface UsageSummarySection {
  title: string
  items: SummaryListItemType[]
}

interface PdfSummary {
  title: string
  validParts: string[]
  lists: Record<
    string,
    {
      value: {
        summaryList?: SummaryListItemType[]
        summaryUsageList?: SummaryListItemType[]
        summaryUsageSections?: UsageSummarySection[]
        generalList?: SummaryListItemType[]
        organisationList?: SummaryListItemType[]
        usageSections?: UsageSummarySection[]
        riskList?: SummaryListItemType[]
        costList?: SummaryListItemType[]
        roleList?: SummaryListItemType[]
        basicServiceList?: SummaryListItemType[]
        additionalServiceList?: SummaryListItemType[]
      } | null
    }
  >
}

export interface PdfVisibilityOptions {
  showRiskSection?: boolean
  showRoleSection?: boolean
}

export class PdfMakeSummaryGenerator {
  project: PdfProject
  summary: PdfSummary
  wtSummary: (...path: string[]) => string
  wtGeneral: (...path: string[]) => string
  wtProjectGeneral: (...path: string[]) => string
  private visibilityOptions: Required<PdfVisibilityOptions>

  constructor(
    project: PdfProject,
    summary: PdfSummary,
    wtSummary: (...path: string[]) => string,
    wtGeneral: (...path: string[]) => string,
    wtProjectGeneral: (...path: string[]) => string,
    visibilityOptions?: PdfVisibilityOptions
  ) {
    this.project = project
    this.summary = summary
    this.wtSummary = wtSummary
    this.wtGeneral = wtGeneral
    this.wtProjectGeneral = wtProjectGeneral
    this.visibilityOptions = {
      showRiskSection: visibilityOptions?.showRiskSection ?? true,
      showRoleSection: visibilityOptions?.showRoleSection ?? true,
    }
  }

  async generate(): Promise<string> {
    const pdfMake = await getPdfMake()
    await setupPdfMakeFonts()
    const filename = this.getFilename()
    const docDefinition = this.buildDocumentDefinition()
    pdfMake.createPdf(docDefinition).download(filename)
    return filename
  }

  async generateBlob(): Promise<{ blob: Blob; filename: string }> {
    const pdfMake = await getPdfMake()
    await setupPdfMakeFonts()
    const filename = this.getFilename(true)
    const docDefinition = this.buildDocumentDefinition()
    const blob = await new Promise<Blob>((resolve) => pdfMake.createPdf(docDefinition).getBlob(resolve))
    return { blob, filename }
  }

  private getFilename(withTime = false): string {
    const now = new Date()
    const pad = (value: number) => String(value).padStart(2, '0')
    // Stored PDFs include the local time so several saves per day stay
    // distinguishable; use the local date alongside it to avoid a UTC/local
    // mismatch around midnight.
    const dateStr = withTime
      ? `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
      : now.toISOString().split('T')[0]
    const suffix = withTime ? `${dateStr}_${pad(now.getHours())}-${pad(now.getMinutes())}` : dateStr
    const projectName = this.summary.title
    return projectName ? `CoreValue_${projectName.replace(/\s/g, '_')}_${suffix}.pdf` : `CoreValue_${suffix}.pdf`
  }

  private getDocumentTitle(): string {
    return (
      this.wtSummary('pdf', 'description', 'documentTitle') || `${this.wtGeneral('navigation', 'summary')} ValueApp`
    )
  }

  private buildDocumentDefinition(): Record<string, unknown> {
    const today = Intl.DateTimeFormat(getCurrentLocale(), { dateStyle: 'long' }).format(new Date())
    const documentTitle = this.getDocumentTitle()

    const content: PdfNode[] = [
      { text: documentTitle, style: 'documentTitle' },
      { text: this.summary.title, style: 'projectTitle', margin: [0, 12, 0, 4] },
      {
        text: (this.wtSummary('pdf', 'description', 'generatedLabel') || 'PDF generated by ValueApp on {date}').replace(
          '{date}',
          today
        ),
        fontSize: 11,
        color: '#737373',
        margin: [0, 6, 0, 20],
      },
    ]

    this.summary.validParts.forEach((part, index) => {
      const list = this.summary.lists[part]?.value || null
      const versionNode = this.createVersionNote(part)
      const partSections: PdfNode[] = []
      const addSection = (section: PdfNode | null) => {
        if (section) {
          partSections.push(section)
        }
      }
      const partStack: PdfNode[] = [
        {
          text: preventTitleWidow(getPartTitle(part, this.wtGeneral)),
          style: 'partTitle',
          margin: [0, 0, 0, 14],
        },
      ]

      if (versionNode) {
        partStack.push({
          ...versionNode,
          alignment: 'left',
          margin: [0, -6, 0, 14],
        })
      }

      if (part === 'general') {
        const generalSectionTitle = this.wtProjectGeneral('details', 'title') || this.wtGeneral('navigation', 'general')
        const generalSection = createListSection(generalSectionTitle, list?.generalList, 'general')
        const summarySection = createListSection(
          this.wtSummary('summary', 'overall', 'title') || 'Overall',
          list?.summaryList,
          'default'
        )
        const organisationSection = createListSection(
          this.wtSummary('pdf', 'project', 'organisation', 'title'),
          list?.organisationList,
          'organisation'
        )
        addSection(generalSection)
        addSection(summarySection)
        const summaryUsageSections = list?.summaryUsageSections?.length
          ? list.summaryUsageSections
          : list?.summaryUsageList?.length
            ? [{ title: this.wtSummary('summary', 'usages', 'title'), items: list.summaryUsageList }]
            : []
        for (const section of summaryUsageSections) {
          addSection(createListSection(section.title, section.items, 'default'))
        }
        addSection(organisationSection)
      } else if (part.endsWith('_setup')) {
        for (const section of list?.usageSections ?? []) {
          const usageSection = createListSection(section.title, section.items, 'setupUsage')
          addSection(usageSection)
        }

        if (this.visibilityOptions.showRiskSection) {
          const riskTitle =
            this.wtSummary('pdf', 'project', 'setup', 'risk', 'sectionTitle') ||
            this.wtSummary('pdf', 'project', 'setup', 'risk', 'title')
          const riskSection = createListSection(riskTitle, list?.riskList, 'setupRisk')
          addSection(riskSection)
        }

        const costTitle =
          this.wtSummary('pdf', 'project', 'setup', 'cost', 'sectionTitle') ||
          this.wtSummary('pdf', 'project', 'setup', 'cost', 'title')
        const costSection = createListSection(costTitle, list?.costList, 'setupCost')
        addSection(costSection)
      } else if (part.endsWith('_service')) {
        if (this.visibilityOptions.showRoleSection) {
          const roleSection = createListSection(
            this.wtSummary('pdf', 'services', 'role', 'title'),
            list?.roleList,
            'serviceRole'
          )
          addSection(roleSection)
        }
        const basicRows = list?.basicServiceList?.slice(0, 2) ?? []
        const phaseRows = list?.basicServiceList?.slice(2) ?? []
        const basicSection = createListSection(
          this.wtSummary('pdf', 'services', 'basicService', 'title'),
          basicRows,
          'serviceBasic'
        )
        const phasesSection = createListSection(
          this.wtSummary('pdf', 'services', 'basicService', 'phase'),
          phaseRows,
          'servicePhases'
        )
        const additionalSection = createListSection(
          this.wtSummary('pdf', 'services', 'additionalService', 'title'),
          list?.additionalServiceList,
          'serviceAdditional'
        )
        addSection(basicSection)
        addSection(phasesSection)
        addSection(additionalSection)
      }

      partStack.push(...trimTrailingSectionSpacing(partSections))

      content.push({
        stack: partStack,
        pageBreak: index === 0 ? undefined : 'before',
      })
    })

    return {
      pageSize: 'A4',
      pageMargins: [40, 46, 40, 46],
      defaultStyle: {
        font: 'DINPro',
        fontSize: 10,
        color: '#404040',
      },
      info: {
        title: this.summary.title,
        subject: documentTitle,
        author: getPdfAuthor(),
      },
      footer: buildFooter(this.wtSummary, today),
      styles: getSharedStyles(),
      content,
    }
  }

  private createVersionNote(part: string): PdfNode | null {
    const version = this.project[part]?.version
    if (!version) {
      return null
    }
    const versionLabel = this.wtSummary('pdf', 'description', 'versionLabel') || 'Version'
    return {
      text: `${versionLabel} ${getPartTitle(part, this.wtGeneral)}: ${version}`,
      style: 'versionText',
      alignment: 'right',
      margin: [0, 6, 0, 0],
    }
  }
}
